import React from "react";
import { Flex, Table, TableContainer, Tbody, Td, Th, Thead, Tr, VisuallyHidden } from "@chakra-ui/react";
import { parse } from "csv-parse/sync";
import { CheckIcon, LegacyCloseIcon } from "ui/icons";

type MdxCompareFeaturesTableProps = {
  csvContent: string;
};

type FeatureRow = {
  featureLabel: string;
  firstFeature: FeatureCell;
  secondFeature: FeatureCell;
};

type FeatureHeader = {
  label: string;
  showLabel: boolean;
};

type FeatureCell = {
  description: string;
  rating: "good" | "neutral" | "bad";
};

/**
 * Custom table used to display feature comparisons for two products.
 * Details on usage can be found in the markdown guide: https://www.notion.so/hightouch/Markdown-Guide-Website-b01528b4df8640069b34fb8666711e55#e35d18e1aed248c792f73515708c8025
 */
export function MdxCompareFeaturesTable({ csvContent }: MdxCompareFeaturesTableProps) {
  const records: string[][] = parse(csvContent);

  const headers = records.shift()!;
  const columnHeaders: FeatureHeader[] = [
    { label: headers[0], showLabel: false },
    { label: headers[1], showLabel: true },
    { label: headers[3], showLabel: true },
  ];

  const rows: FeatureRow[] = records.map((r) => ({
    featureLabel: r[0],
    firstFeature: {
      description: r[1],
      rating: r[2].toLowerCase() as FeatureCell["rating"],
    },
    secondFeature: {
      description: r[3],
      rating: r[4].toLowerCase() as FeatureCell["rating"],
    },
  }));

  return (
    <TableContainer my={8} maxW="100%" overflowX="auto" whiteSpace="break-spaces">
      <Table fontSize="sm">
        <Thead>
          <Tr>
            {columnHeaders.map((header) => (
              <Th key={header.label} color="black" pl="10" fontSize="md" fontWeight="semibold" textTransform="none">
                {header.showLabel ? header.label : <VisuallyHidden>{header.label}</VisuallyHidden>}
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {rows.map((r) => (
            <Tr key={r.featureLabel}>
              <Td fontWeight="semibold" minW="180px" pl="2">
                {r.featureLabel}
              </Td>
              <FeatureData {...r.firstFeature} />
              <FeatureData {...r.secondFeature} />
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
}

function FeatureData({ rating, description }: FeatureCell) {
  return (
    <Td pl="2" minW={{ base: "200px", md: "auto" }}>
      <Flex alignItems="center">
        {rating === "good" ? (
          <CheckIcon width="20px" height="20px" color="grass" mr="3" aria-label="Good." />
        ) : (
          <LegacyCloseIcon width="20px" height="20px" color="product.danger.500" mr="3" aria-label="Bad." />
        )}

        {description}
      </Flex>
    </Td>
  );
}
